"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2, Save } from "lucide-react"
import { isAiReplyEnabled, type MessageAgentStatus } from "@/lib/agents/messaging/types"
import type { MessageAgentWithChannels, UpdateMessageAgentData } from "@/hooks/agents/messaging/use-agents"
import { RoleMultiSelect } from "@/components/agents/shared/role-multi-select"
import { summarizeOfflineChannels } from "@/lib/agents/messaging/channels/shared/offline-message"
import { ComposedPromptDialog } from "@/components/agents/shared/composed-prompt-dialog"
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose"
import { RoleChangeConfirmDialog } from "@/components/agents/shared/role-change-confirm-dialog"
import { AGENT_PRIMARY_ROLE_USE_CASE, DEFAULT_ROLE_SLUG } from "@/lib/ai/roles/types"
import type { AiUseCaseRoleView } from "@/server-actions/ai-use-case-roles"
import { AiDisclosureSettings } from "@/components/organization/ai-disclosure-settings"

const VALID_STATUSES: MessageAgentStatus[] = ["draft", "active", "disabled"]

function isValidStatus(v: string): v is MessageAgentStatus {
  return (VALID_STATUSES as string[]).includes(v)
}

/**
 * The offline-message summary, in the two states where it decides what customers actually get:
 * a disabled agent (every enabled channel answers) and AI Reply off (only channels whose own
 * switch is off answer — the agent-wide switch never triggers an offline message). `null`
 * everywhere else: while the agent replies, the setting is a channel detail, not a headline.
 */
function offlineSummary(
  status: MessageAgentStatus,
  replyEnabled: boolean,
  channels: MessageAgentWithChannels["channels"],
): string | null {
  const silentAgent = status === "disabled" || (status === "active" && !replyEnabled)
  if (!silentAgent) return null

  const { connected, offline } = summarizeOfflineChannels(channels ?? [], {
    agentDisabled: status === "disabled",
  })
  if (connected === 0) return null
  if (offline === 0) {
    return "No connected channel tells customers you are away — an offline message is only sent where the channel's own AI replies switch is off"
  }
  return `Offline message answers on ${offline} of ${connected} connected ${
    connected === 1 ? "channel" : "channels"
  }; the rest stay silent`
}

/**
 * What a customer experiences — hiding this made the statuses guesswork. `active` also depends on
 * the AI Reply tab: with replies switched off there, an active agent sends no AI replies anywhere.
 */
function statusExplanation(status: MessageAgentStatus, replyEnabled: boolean): string {
  switch (status) {
    case "draft":
      return "Nothing reaches customers on any channel — not even an offline message. Use Test to rehearse."
    case "active":
      return replyEnabled
        ? "The agent replies on every connected channel whose AI replies are on."
        : "AI Reply is switched off on the AI Reply tab — no AI replies on any channel. Channels whose own AI replies switch is off still send their Offline message."
    case "disabled":
      return "The agent stops replying. Channels with an offline message keep answering with that text; the rest stay silent."
    default: {
      const exhaustive: never = status
      return exhaustive
    }
  }
}

interface GeneralSettingsProps {
  agent: MessageAgentWithChannels
  /** Opens the Channels tab from the offline-message summary. */
  onOpenChannels?: () => void
  onSave: (id: string, data: UpdateMessageAgentData) => Promise<MessageAgentWithChannels>
  isCreating?: boolean
  onCreate?: (
    name: string,
    roles: string[],
    roleLabel: string | null,
    composed?: ComposedRoleBundle,
  ) => Promise<MessageAgentWithChannels>
  canManage?: boolean
}

export function GeneralSettings({ agent, onSave, isCreating, onCreate, canManage = true, onOpenChannels }: GeneralSettingsProps) {
  const [name, setName] = useState(agent.name || "")
  const [roles, setRoles] = useState<string[]>(agent.config?.roles ?? [DEFAULT_ROLE_SLUG])
  const [roleLabel, setRoleLabel] = useState<string>(agent.config?.role_label ?? "")
  // Held until the user confirms the preset overwrite.
  const [pendingRoles, setPendingRoles] = useState<{ slugs: string[]; names: string[] } | null>(null)
  const [previewRoles, setPreviewRoles] = useState<string[] | null>(null)
  const [isChangingRole, setIsChangingRole] = useState(false)
  const [status, setStatus] = useState<MessageAgentStatus>(agent.status || "draft")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const hasChannels = (agent.channels?.length ?? 0) > 0
  const replyEnabled = isAiReplyEnabled(agent.config)
  // Reads the SELECTED status, so the consequence is visible before saving.
  const summary = offlineSummary(status, replyEnabled, agent.channels)

  useEffect(() => {
    setName(agent.name || "")
    setStatus(agent.status || "draft")
    setRoles(agent.config?.roles ?? [DEFAULT_ROLE_SLUG])
    setRoleLabel(agent.config?.role_label ?? "")
  }, [agent.id, agent.name, agent.status, agent.config?.roles, agent.config?.role_label])

  const applyRoleUpdate = async (
    nextRoles: string[],
    applyPreset: boolean,
    composed?: ComposedRoleBundle,
  ) => {
    setError(null)
    setIsChangingRole(true)
    try {
      await onSave(agent.id, { roles: nextRoles, apply_preset: applyPreset, composed })
      setRoles(nextRoles)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change role")
      // Rethrow: the preview dialog must stay open and keep the composition it
      // already paid for, so a transient failure costs a retry, not a re-generation.
      throw err
    } finally {
      setIsChangingRole(false)
    }
  }

  const handleRolesChange = (nextRoles: string[], roleData: AiUseCaseRoleView[]) => {
    if (isCreating) {
      setRoles(nextRoles)
      return
    }
    // Slugs come from the selection, names only from the loaded presets — a role
    // deleted platform-side must not silently shrink the set on confirm.
    setPendingRoles({
      slugs: nextRoles,
      names: nextRoles.map((slug) => roleData.find((r) => r.slug === slug)?.name ?? slug),
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setIsSaving(true)

    if (!isCreating && status === "active" && !hasChannels) {
      setError("Cannot activate agent without at least one connected channel. Go to the Channels tab to add channels.")
      setIsSaving(false)
      return
    }

    try {
      if (isCreating && onCreate) {
        // Several roles are merged by AI — reviewed before they are applied.
        if (roles.length > 1) {
          setPreviewRoles(roles)
          return
        }
        await onCreate(name, roles, roleLabel.trim() || null)
      } else {
        await onSave(agent.id, { name, status })
        setSuccess(true)
        setTimeout(() => setSuccess(false), 3000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
    <form id="agent-form" onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <AlertDescription className="text-green-800">Settings saved successfully</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Agent Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-x-6 gap-y-4 sm:grid-cols-[3fr_2fr] sm:items-start">
          <div className="space-y-2">
            <Label htmlFor="agent-name">Name *</Label>
            <Input
              id="agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Customer Support Bot"
              maxLength={100}
              required
              disabled={!canManage && !isCreating}
            />
            <p className="text-xs text-gray-500">{name.length}<span>/100</span></p>
          </div>

          <div className="sm:row-span-2">
            <RoleMultiSelect
              useCase={AGENT_PRIMARY_ROLE_USE_CASE.message_agent}
              value={roles}
              onChange={handleRolesChange}
              roleLabel={roleLabel}
              onRoleLabelChange={setRoleLabel}
              onRoleLabelCommit={
                isCreating
                  ? undefined
                  : async (label) => {
                      await onSave(agent.id, { role_label: label || null })
                    }
              }
              disabled={!canManage && !isCreating}
              loading={isChangingRole}
            />
          </div>

          {!isCreating && (
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v)
                }}
                disabled={!canManage}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active" disabled={!hasChannels}>
                    <span>Active</span> <span>{!hasChannels && "(requires channel)"}</span>
                  </SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              {!hasChannels && (
                <p className="text-sm text-yellow-600">
                  Connect at least one channel in the Channels tab to activate this agent.
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                {statusExplanation(status, replyEnabled)}
              </p>
              {summary && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                  {summary}
                  {onOpenChannels && (
                    <>
                      {" — "}
                      <button
                        type="button"
                        onClick={onOpenChannels}
                        className="underline underline-offset-2 hover:no-underline"
                      >
                        manage on the Channels tab
                      </button>
                    </>
                  )}
                  <span>.</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <RoleChangeConfirmDialog
        open={Boolean(pendingRoles)}
        onOpenChange={(open) => !open && setPendingRoles(null)}
        roleNames={pendingRoles?.names ?? []}
        overwrites={["system prompt"]}
        onConfirm={(applyPreset) => {
          const nextRoles = pendingRoles?.slugs ?? []
          setPendingRoles(null)
          if (!nextRoles.length) return
          if (applyPreset && nextRoles.length > 1) {
            setPreviewRoles(nextRoles)
            return
          }
          // The error is already surfaced by applyRoleUpdate; nothing to retry here.
          void applyRoleUpdate(nextRoles, applyPreset).catch(() => {})
        }}
      />

      <ComposedPromptDialog
        open={Boolean(previewRoles)}
        onOpenChange={(open) => !open && setPreviewRoles(null)}
        scopeType="message_agent"
        roleSlugs={previewRoles ?? []}
        onApply={async (composed) => {
          const nextRoles = previewRoles ?? []
          if (isCreating && onCreate) {
            await onCreate(name, nextRoles, roleLabel.trim() || null, composed)
            return
          }
          await applyRoleUpdate(nextRoles, true, composed)
        }}
      />

      {(canManage || isCreating) && (
        <div className="flex justify-end">
          <Button type="submit" disabled={isSaving || !name.trim()}>
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            <span>{isCreating ? "Create Agent" : "Save Changes"}</span>
          </Button>
        </div>
      )}
    </form>
    <AiDisclosureSettings surface="messaging" />
    </div>
  )
}
